import { FC, useMemo } from 'react';

import { excludeForwardProps } from '@gasbi/shared/common/utils/excludeForwardProps';
import { useSelector } from 'react-redux';

import {
  makeSelectStepIsDirty,
  makeSelectStepIsValid,
} from '@/features/actsAndDocuments/actsAndDocumentsSlice';

import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/joy';
import Stack from '@mui/joy/Stack';

import {
  ActsAndDocumentsFormSteps,
  StepKey,
  useActsAndDocumentsFormContext,
} from '@components/ActsAndDocumentsForm';

const getColor = ({
  isActive,
  isInvalid,
  isEditMode,
  theme,
}: {
  isActive: boolean;
  isInvalid: boolean;
  isEditMode?: boolean;
  theme;
}) => {
  if (isInvalid) {
    return theme.palette.danger[500];
  }

  if (isActive) {
    return isEditMode ? theme.palette.success[500] : theme.palette.primary[500];
  }

  return theme.palette.neutral[600];
};

type StyledActFormStepProps = {
  isActive: boolean;
  isInvalid: boolean;
  isEditMode?: boolean;
};

const styledActFormStepPropKeys: readonly (keyof StyledActFormStepProps)[] = [
  'isActive',
  'isInvalid',
  'isEditMode',
] as const;

export const StyledActFormStep = styled(Stack, {
  shouldForwardProp: excludeForwardProps(styledActFormStepPropKeys),
})<StyledActFormStepProps>(({ theme, isActive, isInvalid, isEditMode }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1),
  color: getColor({ isActive, isInvalid, isEditMode, theme }),
  display: 'flex',
  alignItems: 'center',
  fontSize: theme.typography['body-sm'].fontSize,
}));

type ActFormStepProps = {
  title: string;
  step: StepKey<ActsAndDocumentsFormSteps>;
};

export const FormStep = ({
  children,
  isActive = false,
  isInvalid = false,
  isEditMode = false,
  isChecked = false,
}: any) => {
  return (
    <StyledActFormStep
      isActive={isActive}
      isInvalid={isInvalid}
      isEditMode={isEditMode}
      role='button'
    >
      {isChecked ? <CheckIcon /> : <AddIcon />}
      {children}
    </StyledActFormStep>
  );
};

export const ActFormStep: FC<ActFormStepProps> = ({ title, step }) => {
  const { currentStep, isEditMode } = useActsAndDocumentsFormContext();

  const isValid = useSelector(makeSelectStepIsValid(step));
  const isDirty = useSelector(makeSelectStepIsDirty(step));

  const isActive = useMemo(() => currentStep === step, [currentStep, step]);
  const isChecked = useMemo(() => isDirty && isValid, [isDirty, isValid]);

  return (
    <FormStep
      isActive={isActive}
      isInvalid={false}
      isEditMode={isEditMode}
      isChecked={isChecked}
    >
      {title}
    </FormStep>
  );
};
